import React, { useState, useEffect } from 'react'
import './styles.scss'
import { AttachmentSupportChannel } from '@nixi1/nx-channel-utils'
import { MARKETING } from '../../constants'
import { getStaticImage } from 'utils/statics'
import { useSelector } from 'react-redux'
import fragmentText from 'utils/fragmentText'
import { removeEmojisAndAccents as removeEmojisAndAccentsUtil } from 'utils'
import whatsappFormatToHTML from 'utils/whatsappFormatToHTML'
import ChannelAppPreview from './ChannelAppPreview'
import { lang } from 'utils/helpers'

function notSupportedFilesToText(text = '', files) {
  const newText = text.trim()
  if (!files.length) return newText

  const addBr = newText.substring(newText.length - 4).includes("</p>") ? '' : '</br>'

  return newText + files.reduce((preV, elem) => {
    return preV += `<br/><a href="${elem.url || elem.urlLong}" target="_blank">${elem.url || elem.urlLong}</a>`
  }, addBr)
}

function cleanContent(channel, removeEmojisAndAccents, content, notSupportedFiles) {
  const newContent = notSupportedFilesToText(content, notSupportedFiles)

  if (!removeEmojisAndAccents) return newContent
  if (removeEmojisAndAccents && channel !== 'sms') return newContent

  return removeEmojisAndAccentsUtil(newContent)
}

/**
 * PhoneViewer por channels.
 *
 * @param {Object} variables Variables
 * @param {String} channel Channel
 * @param {Object} company Company
 * @param {Object} textPreview Text preview
 * @param {String} origin Origin
 * @param {boolean} previewMsg Preview channel message
 */
// eslint-disable-next-line react/display-name
export default ({ variables = [], channel, company, textPreview = {}, origin, setCurrentChannelPreview, previewMsg, primaryColor }) => {
  const [currentChannel, setCurrentChannel] = useState(channel || 'chatmail')
  const [channelSelector] = useState(channel ? false : true)
  const [template, setTemplate] = useState({})
  const companies = useSelector(state => state.companies)

  const { notSupported, supported } = AttachmentSupportChannel.filesByChannelSync(currentChannel, textPreview.attachments || [])
  const text = cleanContent(currentChannel, textPreview.removeEmojisAndAccents, textPreview.content, notSupported)
  const textHtml = cleanContent(currentChannel, textPreview.removeEmojisAndAccents, textPreview.contentHtml, notSupported)

  useEffect(() => {
    if (origin !== 'marketing') {
      if (currentChannel === 'facebook' || currentChannel === 'instagram') {
        if ((text && text.length <= 80) || !template.limit) {  // Generic template (Default)
          setTemplate({
            type: ['generic'],
            limit: 80,
          })
        }

        if (text && text.length > 80) {  // Normal template
          setTemplate({
            type: ['normal'],
            limit: 2000,
          })
        }

        if (textPreview.buttons && textPreview.buttons.length) {  // Button template
          setTemplate(state => ({
            ...state,
            type: [...state.type, 'button'],
            lastLimit: 640
          }))
        }

        if (supported && supported.length) {  // Media template
          setTemplate(state => ({
            ...state,
            type: [...state.type, 'media'],
            lastLimit: template.type && template.type.find(type => type === 'button') ? 640 : undefined,
          }))
        }
      }

      if (currentChannel === 'telegram') {
        if ((text && text.length <= 1024) || !template.limit) {  // Generic template (Default)
          setTemplate({
            type: ['generic'],
            limit: 4096,
          })
        }

        if (text && text.length > 1024) {  // Normal template
          setTemplate({
            type: ['normal'],
            limit: 4096,
          })
        }
      }

      if (currentChannel === 'sms' || currentChannel === 'whatsapp' || currentChannel === 'whatsapp_whatsapp') {
        if ((text && text.length <= 1500) || !template.limit) {  // Generic template (Default)
          setTemplate({
            type: ['generic'],
            limit: 1500,
          })
        }

        if (text && text.length > 1500) {  // Normal template (Default)
          setTemplate({
            type: ['normal'],
            limit: 1500,
          })
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChannel, textPreview, text])

  useEffect(() => {
    if (origin !== 'marketing') {
      if (text && template.limit) {
        setTemplate(state => ({
          ...state,
          content: fragmentText(text, { limit: template.limit, lastLimit: template.lastLimit })
        }))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textPreview, template.limit, template.lastLimit])

  const handleSetPhoneTextPreview = (values, notSupportedFiles) => {
    let contentStr = ''
    if (Object.values(values).length >= 0) {
      if (values.variables) {
        contentStr = values.variables.reduce((a, c) => {
          const v = variables.find((v) => v.value === c.field)
          if (v && v.example) {
            a = a.replaceAll(c.name, v.example.text)
          }
          return a
        }, values.contentHtml || values.content || '')
      } else {
        contentStr = values.contentHtml || values.content || ''
      }
    }

    return notSupportedFilesToText(contentStr, notSupportedFiles)
  }

  const handleSetTextHeader = (values, notSupportedFiles) => {
    let contentStr = ''
    if (Object.values(values).length >= 0) {
      if (values.variablesHeader) {
        const v = variables.find((v) => v.value === values.variablesHeader.field)
        if (v && v.example) 
          values.textHeader = values.textHeader.replaceAll(values.variablesHeader.name, v.example.text)
        contentStr = values.textHeader
      } else { 
        contentStr = values.textHeader || ''
      }
    }
    return notSupportedFilesToText(contentStr, notSupportedFiles)
  }

  const previewFragment = (values) => {
    const files = supported.length > 0 ? supported[0] : null

    if (template.content && template.type) {
      const firstFragments = template.content.slice(0, template.content.length - 1) || []
      const lastFragment = template.content.slice(-1) || []
      if (currentChannel === 'instagram') {
        if (template.type.find(type => type === 'generic')) {
          return (
            <>
              <div className={`rectangle ${currentChannel}`}>
                {files && !files.withVirus && files.mimeType === 'image' && values.buttons && values.buttons.length ? (
                  <div key={files.urlLong} className='image'>
                    <img src={files.urlLong} alt={currentChannel} />
                  </div>
                ) : null}
                {files && !files.withVirus && files.mimeType === 'video' && values.buttons && values.buttons.length ? (
                  <div key={files.urlLong} className='image'>
                    <video key={files.urlLong} src={files.urlLong} controls />
                  </div>
                ) : null}
                {lastFragment[0] && (
                  <div className='inner'>
                    <p
                      style={{ whiteSpace: 'pre-line' }}
                      dangerouslySetInnerHTML={{ __html: lastFragment[0] }}
                    >
                    </p>
                  </div>
                )}
                {values.buttons && values.buttons.length ? (
                  <div className='inner-buttons preview-fragment-channels'>
                    {values.buttons.map((it, index) => (
                      <div className='buttons' key={`button-${index}`}>
                        <span>{it.label}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
              <div className={`rectangle ${currentChannel}`}>
                {files && !files.withVirus && files.mimeType === 'image' && values.buttons === undefined ?(
                  <div key={files.urlLong} className='image'>
                    <img src={files.urlLong} alt={currentChannel} />
                  </div>
                ):null}
                {files && !files.withVirus && files.mimeType === 'video' && values.buttons === undefined ?(
                  <div key={files.urlLong} className='image'>
                    <video key={files.urlLong} src={files.urlLong} controls />
                  </div>
                ):null}
                {files && !files.withVirus && files.mimeType === 'image' && values.buttons && !values.buttons.length ? (
                  <div key={files.urlLong} className='image'>
                    <img src={files.urlLong} alt={currentChannel} />
                  </div>
                ) : null}
                {
                  files && !files.withVirus && files.mimeType === 'video' && values.buttons && !values.buttons.length ? (
                    <div key={files.urlLong} className='image'>
                      <video key={files.urlLong} src={files.urlLong} controls />
                    </div>
                  ):null
                }
              </div>
            </>
          )
        } else if (template.type.find(type => type === 'normal')) {
          if (!template.type.find(type => type === 'media') && template.type.find(type => type === 'button')) {
            return (
              <>
                {firstFragments.map((fragment, index) => (
                  <div className={`rectangle ${currentChannel}`} key={index + 1}>
                    <div className='inner'>
                      <p
                        style={{ whiteSpace: 'pre-line' }}
                        dangerouslySetInnerHTML={{ __html: fragment }}
                      >
                      </p>
                    </div>
                  </div>
                ))}
                <div className={`rectangle ${currentChannel}`}>
                  {(lastFragment && lastFragment.length) || values.buttons ? (
                    <div className='inner'>
                      <p
                        style={{ whiteSpace: 'pre-line' }}
                        dangerouslySetInnerHTML={{ __html: lastFragment[0] }}
                      >
                      </p>
                    </div>
                  ) : null}
                  {values.buttons.length ? (
                    <div className='inner-buttons'>
                      {values.buttons.map((it, index) => (
                        <div className='buttons' key={`button-${index}`}>
                          <span>{it.label}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              </>
            )
          } else if (!template.type.find(type => type === 'button') && template.type.find(type => type === 'media')) {
            return (
              <>
                {files && !files.withVirus ? (
                  <div className={`rectangle ${currentChannel}`}>
                    {files.mimeType === 'image' && (
                      <div key={files.urlLong} className='image'>
                        <img src={files.urlLong} alt={currentChannel} />
                      </div>
                    )}
                    {files.mimeType === 'video' && (
                      <div key={files.urlLong} className='image'>
                        <video key={files.urlLong} src={files.urlLong} controls />
                      </div>
                    )}
                  </div>
                ) : null}
                {template.content.map((fragment, index) => (
                  <div className={`rectangle ${currentChannel}`} key={index + 1}>
                    <div className='inner'>
                      <p
                        style={{ whiteSpace: 'pre-line' }}
                        dangerouslySetInnerHTML={{ __html: fragment }}
                      >
                      </p>
                    </div>
                  </div>
                ))}
              </>
            )
          } else {
            return (
              <>
                {files && !files.withVirus ? (
                  <div className={`rectangle ${currentChannel}`}>
                    {files.mimeType === 'image' && (
                      <div key={files.urlLong} className='image'>
                        <img src={files.urlLong} alt={currentChannel} />
                      </div>
                    )}
                    {files.mimeType === 'video' && (
                      <div key={files.urlLong} className='image'>
                        <video key={files.urlLong} src={files.urlLong} controls />
                      </div>
                    )}
                  </div>
                ) : null}
                {firstFragments.map((fragment, index) => (
                  <div className={`rectangle ${currentChannel}`} key={index + 1}>
                    <div className='inner'>
                      <p
                        style={{ whiteSpace: 'pre-line' }}
                        dangerouslySetInnerHTML={{ __html: fragment }}
                      >
                      </p>
                    </div>
                  </div>
                ))}
                <div className={`rectangle ${currentChannel}`} >
                  {lastFragment && lastFragment.length ? (
                    <div className='inner'>
                      <p
                        style={{ whiteSpace: 'pre-line' }}
                        dangerouslySetInnerHTML={{ __html: lastFragment[0] }}
                      >
                      </p>
                    </div>
                  ) : null}
                  {values.buttons && values.buttons.length ? (
                    <div className='inner-buttons'>
                      {values.buttons.map((it, index) => (
                        <div className='buttons' key={`button-${index}`}>
                          <span>{it.label}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              </>
            )
          }
        } else {
          return null
        }
      }

      if (currentChannel === 'facebook') {
        if (template.type.find(type => type === 'generic')) {
          return (
            <>
              <div className={`rectangle ${currentChannel}`}>
                {files && !files.withVirus && files.mimeType === 'image' && (
                  <div key={files.urlLong} className='image'>
                    <img src={files.urlLong} alt={currentChannel} />
                  </div>
                )}
                {files && !files.withVirus && files.mimeType === 'video' && (
                  <div key={files.urlLong} className='image'>
                    <video key={files.urlLong} src={files.urlLong} controls />
                  </div>
                )}
                {lastFragment[0] && (
                  <div className='inner'>
                    <p
                      style={{ whiteSpace: 'pre-line' }}
                      dangerouslySetInnerHTML={{ __html: lastFragment[0] }}
                    >
                    </p>
                  </div>
                )}
                {values.buttons && values.buttons.length ? (
                  <div className='inner-buttons'>
                    {values.buttons.map((it, index) => (
                      <div className='buttons' key={`button-${index}`}>
                        <span>{it.label}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </>
          )
        } else if (template.type.find(type => type === 'normal')) {
          if (!template.type.find(type => type === 'media') && template.type.find(type => type === 'button')) {
            return (
              <>
                {firstFragments.map((fragment, index) => (
                  <div className={`rectangle ${currentChannel}`} key={index + 1}>
                    <div className='inner'>
                      <p
                        style={{ whiteSpace: 'pre-line' }}
                        dangerouslySetInnerHTML={{ __html: fragment }}
                      >
                      </p>
                    </div>
                  </div>
                ))}
                <div className={`rectangle ${currentChannel}`}>
                  {(lastFragment && lastFragment.length) || values.buttons ? (
                    <div className='inner'>
                      <p
                        style={{ whiteSpace: 'pre-line' }}
                        dangerouslySetInnerHTML={{ __html: lastFragment[0] }}
                      >
                      </p>
                    </div>
                  ) : null}
                  {values.buttons.length ? (
                    <div className='inner-buttons'>
                      {values.buttons.map((it, index) => (
                        <div className='buttons' key={`button-${index}`}>
                          <span>{it.label}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              </>
            )
          } else if (!template.type.find(type => type === 'button') && template.type.find(type => type === 'media')) {
            return (
              <>
                {files && !files.withVirus ? (
                  <div className={`rectangle ${currentChannel}`}>
                    {files.mimeType === 'image' && (
                      <div key={files.urlLong} className='image'>
                        <img src={files.urlLong} alt={currentChannel} />
                      </div>
                    )}
                    {files.mimeType === 'video' && (
                      <div key={files.urlLong} className='image'>
                        <video key={files.urlLong} src={files.urlLong} controls />
                      </div>
                    )}
                  </div>
                ) : null}
                {template.content.map((fragment, index) => (
                  <div className={`rectangle ${currentChannel}`} key={index + 1}>
                    <div className='inner'>
                      <p
                        style={{ whiteSpace: 'pre-line' }}
                        dangerouslySetInnerHTML={{ __html: fragment }}
                      >
                      </p>
                    </div>
                  </div>
                ))}
              </>
            )
          } else {
            return (
              <>
                {files && !files.withVirus ? (
                  <div className={`rectangle ${currentChannel}`}>
                    {files.mimeType === 'image' && (
                      <div key={files.urlLong} className='image'>
                        <img src={files.urlLong} alt={currentChannel} />
                      </div>
                    )}
                    {files.mimeType === 'video' && (
                      <div key={files.urlLong} className='image'>
                        <video key={files.urlLong} src={files.urlLong} controls />
                      </div>
                    )}
                  </div>
                ) : null}
                {firstFragments.map((fragment, index) => (
                  <div className={`rectangle ${currentChannel}`} key={index + 1}>
                    <div className='inner'>
                      <p
                        style={{ whiteSpace: 'pre-line' }}
                        dangerouslySetInnerHTML={{ __html: fragment }}
                      >
                      </p>
                    </div>
                  </div>
                ))}
                <div className={`rectangle ${currentChannel}`} >
                  {lastFragment && lastFragment.length ? (
                    <div className='inner'>
                      <p
                        style={{ whiteSpace: 'pre-line' }}
                        dangerouslySetInnerHTML={{ __html: lastFragment[0] }}
                      >
                      </p>
                    </div>
                  ) : null}
                  {values.buttons && values.buttons.length ? (
                    <div className='inner-buttons'>
                      {values.buttons.map((it, index) => (
                        <div className='buttons' key={`button-${index}`}>
                          <span>{it.label}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              </>
            )
          }
        } else {
          return null
        }
      }

      if (currentChannel === 'telegram') {
        if (template.type.find(type => type === 'generic')) {
          return (
            <>
              {firstFragments.map((fragment, index) => (
                <div className={`rectangle ${currentChannel}`} key={index + 1}>
                  <div className='inner'>
                    <p
                      style={{ whiteSpace: 'pre-line' }}
                      dangerouslySetInnerHTML={{ __html: fragment }}
                    >
                    </p>
                  </div>
                </div>
              ))}
              <div className={`rectangle ${currentChannel}`}>
                {files && !files.withVirus && files.mimeType === 'image' && (
                  <div key={files.urlLong} className='image'>
                    <img src={files.urlLong} alt={currentChannel} />
                  </div>
                )}
                {files && !files.withVirus && files.mimeType === 'video' && (
                  <div key={files.urlLong} className='image'>
                    <video key={files.urlLong} src={files.urlLong} controls />
                  </div>
                )}
                {files && !files.withVirus && files.mimeType !== 'video' && files.mimeType !== 'image' && (
                  <div key={files.urlLong} className='file'>
                    <img src={'./img/pdf-default.png'} alt={currentChannel} />
                  </div>
                )}
                {lastFragment[0] && (
                  <div className='inner'>
                    <p
                      style={{ whiteSpace: 'pre-line' }}
                      dangerouslySetInnerHTML={{ __html: lastFragment[0] }}
                    >
                    </p>
                  </div>
                )}
                {values.buttons && values.buttons.length ? (
                  <div className='inner-buttons'>
                    {values.buttons.map((it, index) => (
                      <div className='buttons' key={`button-${index}`}>
                        <span>{it.label}</span>
                        <img src='./img/icons/arrow-up-right.svg' alt='telegram' />
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </>
          )
        } else {
          return (
            <>
              <div className={`rectangle ${currentChannel}`}>
                {files && !files.withVirus && files.mimeType === 'image' && (
                  <div key={files.urlLong} className='image'>
                    <img src={files.urlLong} alt={currentChannel} />
                  </div>
                )}
                {files && !files.withVirus && files.mimeType === 'video' && (
                  <div key={files.urlLong} className='image'>
                    <video key={files.urlLong} src={files.urlLong} controls />
                  </div>
                )}
                {files && !files.withVirus && files.mimeType !== 'video' && files.mimeType !== 'image' && (
                  <div key={files.urlLong} className='file'>
                    <img src={'./img/pdf-default.png'} alt={currentChannel} />
                  </div>
                )}
              </div>
              {firstFragments.map((fragment, index) => (
                <div className={`rectangle ${currentChannel}`} key={index + 1}>
                  <div className='inner'>
                    <p
                      style={{ whiteSpace: 'pre-line' }}
                      dangerouslySetInnerHTML={{ __html: fragment }}
                    >
                    </p>
                  </div>
                </div>
              ))}
              <div className={`rectangle ${currentChannel}`}>
                {lastFragment[0] && (
                  <div className='inner'>
                    <p
                      style={{ whiteSpace: 'pre-line' }}
                      dangerouslySetInnerHTML={{ __html: lastFragment[0] }}
                    >
                    </p>
                  </div>
                )}
                {values.buttons && values.buttons.length ? (
                  <div className='inner-buttons'>
                    {values.buttons.map((it, index) => (
                      <div className='buttons' key={`button-${index}`}>
                        <span>{it.label}</span>
                        <img src='./img/icons/arrow-up-right.svg' alt='telegram' />
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </>
          )
        }
      }

      if (currentChannel === 'sms') {
        if (template.type.find(type => type === 'generic')) {
          return (
            <>
              {firstFragments.map((fragment, index) => (
                <div className={`rectangle ${currentChannel}`} key={index + 1}>
                  <div className='inner'>
                    <p
                      style={{ whiteSpace: 'pre-line' }}
                      dangerouslySetInnerHTML={{ __html: fragment }}
                    >
                    </p>
                  </div>
                </div>
              ))}
              <div className={`rectangle ${currentChannel}`}>
                {lastFragment[0] && (
                  <div className='inner'>
                    <p
                      style={{ whiteSpace: 'pre-line' }}
                      dangerouslySetInnerHTML={{ __html: lastFragment[0] }}
                    >
                    </p>
                  </div>
                )}
                {values.buttons && values.buttons.length ? (
                  <div className='inner-buttons'>
                    {values.buttons.map((it, index) => (
                      <div className='buttons' key={`button-${index}`}>
                        <p>{it.label}</p>
                        <a href={it.content} target='blank'>
                          {it.content}
                        </a>
                      </div>
                    ))}
                  </div>
                ) : null}
                {files && !files.withVirus && (
                  <div className='links'>
                    <a href={files.url} target='blank'>
                      {files.url}
                    </a>
                  </div>
                )}
                {files && !files.withVirus && files.mimeType === 'image' && (
                  <div key={files.urlLong} className='file'>
                    <img src={files.urlLong} alt={currentChannel} />
                    <a href={files.url} target='blank'>
                      {files.url}
                    </a>
                  </div>
                )}
                {files && !files.withVirus && files.mimeType === 'video' && (
                  <div key={files.urlLong} className='file'>
                    <video key={files.urlLong} src={files.urlLong} />
                    <a href={files.url} target='blank'>
                      {files.url}
                    </a>
                  </div>
                )}
                {files && !files.withVirus && files.mimeType !== 'video' && files.mimeType !== 'image' && (
                  <div key={files.urlLong} className='file'>
                    <img src={'./img/pdf-default.png'} alt={currentChannel} className='pdf' />
                    <a href={files.url} target='blank'>
                      {files.url}
                    </a>
                  </div>
                )}
              </div>
            </>
          )
        } else {
          return (
            <>
              <div className={`rectangle ${currentChannel}`}>
                {files && files.mimeType === 'image' && (
                  <div key={files.urlLong} className='file'>
                    <img src={files.urlLong} alt={currentChannel} />
                    <a href={files.url} target='blank'>
                      {files.url}
                    </a>
                  </div>
                )}
                {files && !files.withVirus && files.mimeType === 'video' && (
                  <div key={files.urlLong} className='file'>
                    <video key={files.urlLong} src={files.urlLong} />
                    <a href={files.url} target='blank'>
                      {files.url}
                    </a>
                  </div>
                )}
                {files &&  !files.withVirus &&files.mimeType !== 'video' && files.mimeType !== 'image' && (
                  <div key={files.urlLong} className='file'>
                    <img src={'./img/pdf-default.png'} alt={currentChannel} className='pdf' />
                    <a href={files.url} target='blank'>
                      {files.url}
                    </a>
                  </div>
                )}
              </div>
              {firstFragments.map((fragment, index) => (
                <div className={`rectangle ${currentChannel}`} key={index + 1}>
                  <div className='inner'>
                    <p
                      style={{ whiteSpace: 'pre-line' }}
                      dangerouslySetInnerHTML={{ __html: fragment }}
                    >
                    </p>
                  </div>
                </div>
              ))}
              <div className={`rectangle ${currentChannel}`}>
                {lastFragment[0] && (
                  <div className='inner'>
                    <p
                      style={{ whiteSpace: 'pre-line' }}
                      dangerouslySetInnerHTML={{ __html: lastFragment[0] }}
                    >
                    </p>
                  </div>
                )}
                {values.buttons && values.buttons.length ? (
                  <div className='inner-buttons'>
                    {values.buttons.map((it, index) => (
                      <div className='buttons' key={`button-${index}`}>
                        <p>{it.label}</p>
                        <a href={it.content} target='blank'>
                          {it.content}
                        </a>
                      </div>
                    ))}
                  </div>
                ) : null}
                {files && !files.withVirus && (
                  <div className='links'>
                    <a href={files.url} target='blank'>
                      {files.url}
                    </a>
                  </div>
                )}
              </div>
            </>
          )
        }
      }

      if (currentChannel === 'whatsapp') {
        if (template.type.find(type => type === 'generic')) {
          return (
            <>
              {firstFragments.map((fragment, index) => (
                <div className={`rectangle ${currentChannel}`} key={index + 1}>
                  <div className='inner'>
                    <p
                      style={{ whiteSpace: 'pre-line' }}
                      dangerouslySetInnerHTML={{ __html: whatsappFormatToHTML(fragment) }}
                    >
                    </p>
                  </div>
                </div>
              ))}
              <div className={`rectangle ${currentChannel}`}>
                {files && !files.withVirus && files.mimeType === 'image' && (
                  <div key={files.urlLong} className='file'>
                    <img src={files.urlLong} alt={currentChannel} />
                    <a href={files.url} target='blank'>
                      {files.url}
                    </a>
                  </div>
                )}
                {files && !files.withVirus && files.mimeType === 'video' && (
                  <div key={files.urlLong} className='file'>
                    <video key={files.urlLong} src={files.urlLong} />
                    <a href={files.url} target='blank'>
                      {files.url}
                    </a>
                  </div>
                )}
                {files && !files.withVirus && files.mimeType !== 'video' && files.mimeType !== 'image' && (
                  <div key={files.urlLong} className='file'>
                    <img src={'./img/pdf-default.png'} alt={currentChannel} />
                    <a href={files.url} target='blank'>
                      {files.url}
                    </a>
                  </div>
                )}
                {lastFragment[0] && (
                  <div className='inner'>
                    <p
                      style={{ whiteSpace: 'pre-line' }}
                      dangerouslySetInnerHTML={{ __html: whatsappFormatToHTML(lastFragment[0]) }}
                    >
                    </p>
                  </div>
                )}
                {files && !files.withVirus && (
                  <>
                    <a href={files.url} target='blank'>
                      {files.url}
                    </a>
                  </>
                )}
                {values.buttons && values.buttons.length ? (
                  <div className='inner-buttons'>
                    {values.buttons.map((it, index) => (
                      <div className='buttons' key={`button-${index}`}>
                        <p>{it.label}</p>
                        <a href={it.content} target='blank'>
                          {it.content}
                        </a>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </>
          )
        } else {
          return (
            <>
              <div className={`rectangle ${currentChannel}`}>
                {files && !files.withVirus && files.mimeType === 'image' && (
                  <div key={files.urlLong} className='file'>
                    <img src={files.urlLong} alt={currentChannel} />
                    <a href={files.url} target='blank'>
                      {files.url}
                    </a>
                  </div>
                )}
                {files && !files.withVirus && files.mimeType === 'video' && (
                  <div key={files.urlLong} className='file'>
                    <video key={files.urlLong} src={files.urlLong} />
                    <a href={files.url} target='blank'>
                      {files.url}
                    </a>
                  </div>
                )}
                {files && !files.withVirus && files.mimeType !== 'video' && files.mimeType !== 'image' && (
                  <div key={files.urlLong} className='file'>
                    <img src={'./img/pdf-default.png'} alt={currentChannel} />
                    <a href={files.url} target='blank'>
                      {files.url}
                    </a>
                  </div>
                )}
              </div>
              {firstFragments.map((fragment, index) => (
                <div className={`rectangle ${currentChannel}`} key={index + 1}>
                  <div className='inner'>
                    <p
                      style={{ whiteSpace: 'pre-line' }}
                      dangerouslySetInnerHTML={{ __html: whatsappFormatToHTML(fragment) }}
                    >
                    </p>
                  </div>
                </div>
              ))}
              <div className={`rectangle ${currentChannel}`}>
                {lastFragment[0] && (
                  <div className='inner'>
                    <p
                      style={{ whiteSpace: 'pre-line' }}
                      dangerouslySetInnerHTML={{ __html: whatsappFormatToHTML(lastFragment[0]) }}
                    >
                    </p>
                  </div>
                )}
                {values.buttons && values.buttons.length ? (
                  <div className='inner-buttons'>
                    {values.buttons.map((it, index) => (
                      <div className='buttons' key={`button-${index}`}>
                        <p>{it.label}</p>
                        <a href={it.content} target='blank'>
                          {it.content}
                        </a>
                      </div>
                    ))}
                  </div>
                ) : null}
                {files && !files.withVirus && (
                  <>
                    <a href={files.url} target='blank'>
                      {files.url}
                    </a>
                  </>
                )}
              </div>
            </>
          )
        }
      }

      if (currentChannel === 'whatsapp_whatsapp') {
        if (template.type.find(type => type === 'generic')) {
          return (
            <>
              {firstFragments.map((fragment, index) => (
                <div className={`rectangle ${currentChannel}`} key={index + 1}>
                  <div className='inner'>
                    <p
                      style={{ whiteSpace: 'pre-line', border: 'red solid 1px' }}
                      dangerouslySetInnerHTML={{ __html: whatsappFormatToHTML(fragment) }}
                    >
                    </p>
                  </div>
                </div>
              ))}
              <div className={`rectangle ${currentChannel}`}>
                {files && !files.withVirus && files.mimeType === 'image' && (
                  <div key={files.urlLong} className='file'>
                    <img src={files.urlLong} alt={currentChannel} />
                    <a href={files.url} target='blank'>
                      {files.url}
                    </a>
                  </div>
                )}
                {files && !files.withVirus && files.mimeType === 'video' && (
                  <div key={files.urlLong} className='file'>
                    <video key={files.urlLong} src={files.urlLong} />
                    <a href={files.url} target='blank'>
                      {files.url}
                    </a>
                  </div>
                )}
                {files && !files.withVirus && files.mimeType !== 'video' && files.mimeType !== 'image' && (
                  <div key={files.urlLong} className='file'>
                    <img src={'./img/pdf-default.png'} alt={currentChannel} />
                    <a href={files.url} target='blank'>
                      {files.url}
                    </a>
                  </div>
                )}
                {lastFragment[0] && (
                  <div className='inner'>
                    <p
                      style={{ whiteSpace: 'pre-line', border: 'green solid 1px' }}
                      dangerouslySetInnerHTML={{ __html: whatsappFormatToHTML(lastFragment[0]) }}
                    >
                    </p>
                  </div>
                )}
                {files && !files.withVirus && (
                  <>
                    <a href={files.url} target='blank'>
                      {files.url}
                    </a>
                  </>
                )}
                {values.buttons && values.buttons.length ? (
                  <div className='inner-buttons'>
                    {values.buttons.map((it, index) => (
                      <div className='buttons' key={`button-${index}`}>
                        <p>{it.label}</p>
                        <a href={it.content} target='blank'>
                          {it.content}
                        </a>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </>
          )
        } else {
          return (
            <>
              <div className={`rectangle ${currentChannel}`}>
                {files && !files.withVirus && files.mimeType === 'image' && (
                  <div key={files.urlLong} className='file'>
                    <img src={files.urlLong} alt={currentChannel} />
                    <a href={files.url} target='blank'>
                      {files.url}
                    </a>
                  </div>
                )}
                {files && !files.withVirus && files.mimeType === 'video' && (
                  <div key={files.urlLong} className='file'>
                    <video key={files.urlLong} src={files.urlLong} />
                    <a href={files.url} target='blank'>
                      {files.url}
                    </a>
                  </div>
                )}
                {files && !files.withVirus && files.mimeType !== 'video' && files.mimeType !== 'image' && (
                  <div key={files.urlLong} className='file'>
                    <img src={'./img/pdf-default.png'} alt={currentChannel} />
                    <a href={files.url} target='blank'>
                      {files.url}
                    </a>
                  </div>
                )}
              </div>
              {firstFragments.map((fragment, index) => (
                <div className={`rectangle ${currentChannel}`} key={index + 1}>
                  <div className='inner'>
                    <p
                      style={{ whiteSpace: 'pre-line' }}
                      dangerouslySetInnerHTML={{ __html: whatsappFormatToHTML(fragment) }}
                    >
                    </p>
                  </div>
                </div>
              ))}
              <div className={`rectangle ${currentChannel}`}>
                {lastFragment[0] && (
                  <div className='inner'>
                    <p
                      style={{ whiteSpace: 'pre-line' }}
                      dangerouslySetInnerHTML={{ __html: whatsappFormatToHTML(lastFragment[0]) }}
                    >
                    </p>
                  </div>
                )}
                {values.buttons && values.buttons.length ? (
                  <div className='inner-buttons'>
                    {values.buttons.map((it, index) => (
                      <div className='buttons' key={`button-${index}`}>
                        <p>{it.label}</p>
                        <a href={it.content} target='blank'>
                          {it.content}
                        </a>
                      </div>
                    ))}
                  </div>
                ) : null}
                {files && !files.withVirus && (
                  <>
                    <a href={files.url} target='blank'>
                      {files.url}
                    </a>
                  </>
                )}
              </div>
            </>
          )
        }
      }
    } else {
      return null
    }
  }

  const preview = (values) => {
    const files = supported.length > 0 ? supported[0] : null

    if (currentChannel === 'facebook') {
      return (
        <div className={`rectangle ${currentChannel}`}>
          {files && !files.withVirus && files.mimeType === 'image' && (
            <div key={files.urlLong} className='image'>
              <img src={files.urlLong} alt={currentChannel} />
            </div>
          )}
          {files && !files.withVirus && files.mimeType === 'video' && (
            <div key={files.urlLong} className='image'>
              <video key={files.urlLong} src={files.urlLong} controls />
            </div>
          )}
          {handleSetPhoneTextPreview(textPreview, notSupported) && (
            <div className='inner'>
              <p
                style={{ whiteSpace: textHtml ? 'normal' : 'pre-line' }}
                dangerouslySetInnerHTML={{ __html: handleSetPhoneTextPreview(textPreview, notSupported) }}
              ></p>
            </div>
          )}
          {values.buttons && values.buttons.length ? (
            <div className='inner-buttons'>
              {values.buttons.map((it, index) => (
                <div className='buttons' key={`button-${index}`}>
                  <span>{it.label}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      )
    } else if (currentChannel === 'telegram') {
      return (
        <div className={`rectangle ${currentChannel}`}>
          {files && !files.withVirus && files.mimeType === 'image' && (
            <div key={files.urlLong} className='image'>
              <img src={files.urlLong} alt={currentChannel} />
            </div>
          )}
          {files && !files.withVirus && files.mimeType === 'video' && (
            <div key={files.urlLong} className='image'>
              <video key={files.urlLong} src={files.urlLong} controls />
            </div>
          )}
          {files && !files.withVirus && files.mimeType !== 'video' && files.mimeType !== 'image' && (
            <div key={files.urlLong} className='file'>
              <img src={'./img/pdf-default.png'} alt={currentChannel} />
            </div>
          )}
          {handleSetPhoneTextPreview(textPreview, notSupported) && (
            <div className='inner'>
              <p
                style={{ whiteSpace: textHtml ? 'normal' : 'pre-line' }}
                dangerouslySetInnerHTML={{ __html: handleSetPhoneTextPreview(textPreview, notSupported) }}
              ></p>
            </div>
          )}
          {values.buttons && values.buttons.length ? (
            <div className='inner-buttons'>
              {values.buttons.map((it, index) => (
                <div className='buttons' key={`button-${index}`}>
                  <span>{it.label}</span>
                  <img src='./img/icons/arrow-up-right.svg' alt='telegram' />
                </div>
              ))}
            </div>
          ) : null}
        </div>
      )
    } else if (currentChannel === 'sms') {
      return (
        <div className={`rectangle ${currentChannel}`}>
          {handleSetPhoneTextPreview(textPreview, notSupported) && (
            <div className='inner'>
              <p
                style={{ whiteSpace: textHtml ? 'normal' : 'pre-line' }}
                dangerouslySetInnerHTML={{ __html: handleSetPhoneTextPreview(textPreview, notSupported) }}
              ></p>
            </div>
          )}
          {values.buttons && values.buttons.length ? (
            <div className='inner-buttons'>
              {values.buttons.map((it, index) => (
                <div className='buttons' key={`button-${index}`}>
                  <p>{it.label}</p>
                  <a href={it.content} target='blank'>
                    {it.content}
                  </a>
                </div>
              ))}
            </div>
          ) : null}
          {files && !files.withVirus && (
            <div className='links'>
              <a href={files.url} target='blank'>
                {files.url}
              </a>
            </div>
          )}
          {files && !files.withVirus && files.mimeType === 'image' && (
            <div key={files.urlLong} className='file'>
              <img src={files.urlLong} alt={currentChannel} />
              <a href={files.url} target='blank'>
                {files.url}
              </a>
            </div>
          )}
          {files && !files.withVirus && files.mimeType === 'video' && (
            <div key={files.urlLong} className='file'>
              <video key={files.urlLong} src={files.urlLong} />
              <a href={files.url} target='blank'>
                {files.url}
              </a>
            </div>
          )}
          {files && !files.withVirus && files.mimeType !== 'video' && files.mimeType !== 'image' && (
            <div key={files.urlLong} className='file'>
              <img src={'./img/pdf-default.png'} alt={currentChannel} className='pdf' />
              <a href={files.url} target='blank'>
                {files.url}
              </a>
            </div>
          )}
        </div>
      )
    } else if (currentChannel === 'whatsapp') {
      return (
        <div className={`rectangle ${currentChannel}`}>
          {files && files.mimeType === 'image' && (
            <div key={files.urlLong} className='file'>
              <img src={files.urlLong} alt={currentChannel} />
              <a href={files.url} target='blank'>
                {files.url}
              </a>
            </div>
          )}
          {files && !files.withVirus && files.mimeType === 'video' && (
            <div key={files.urlLong} className='file'>
              <video key={files.urlLong} src={files.urlLong} />
              <a href={files.url} target='blank'>
                {files.url}
              </a>
            </div>
          )}
          {files && !files.withVirus && files.mimeType !== 'video' && files.mimeType !== 'image' && (
            <div key={files.urlLong} className='file'>
              <img src={'./img/pdf-default.png'} alt={currentChannel} />
              <a href={files.url} target='blank'>
                {files.url}
              </a>
            </div>
          )}
          {handleSetPhoneTextPreview(textPreview, notSupported) && (
            <div className='inner'>
              <p
                style={{ whiteSpace: textHtml ? 'normal' : 'pre-line' }}
                dangerouslySetInnerHTML={{ __html: handleSetPhoneTextPreview(textPreview, notSupported) }}
              ></p>
            </div>
          )}
          {files && !files.withVirus && (
            <>
              <a href={files.url} target='blank'>
                {files.url}
              </a>
            </>
          )}
          {values.buttons && values.buttons.length ? (
            <div className='inner-buttons'>
              {values.buttons.map((it, index) => (
                <div className='buttons' key={`button-${index}`}>
                  <p>{it.label}</p>
                  <a href={it.content} target='blank'>
                    {it.content}
                  </a>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      )
    } else if (currentChannel === 'whatsapp_whatsapp') {
      return (
        <>
          {textPreview.content && textPreview.content !== '' && (
            <div className={`rectangle ${currentChannel}`}>
              {files && !files.withVirus && files.mimeType === 'image' && (
                <div key={files.urlLong} className='file_whatsapp_whatsapp'>
                  <img src={files.urlLong} alt={currentChannel} />
                </div>
              )}
              {files && !files.withVirus && files.mimeType === 'video' && (
                <div key={files.urlLong} className='file_whatsapp_whatsapp'>
                  <video key={files.urlLong} src={files.urlLong} />
                </div>
              )}
              {files && !files.withVirus && files.mimeType !== 'video' && files.mimeType !== 'image' && (
                <div key={files.urlLong} className='file_whatsapp_whatsapp'>
                  <img src={'./img/pdf-default.png'} alt={currentChannel} />
                </div>
              )}
              {values.textHeader && handleSetTextHeader(textPreview, notSupported) && (
                <div className='inner'>
                  <strong
                    style={{ whiteSpace: textHtml ? 'normal' : 'pre-line' }}
                    dangerouslySetInnerHTML={{ __html: handleSetTextHeader(textPreview, notSupported) }}
                  ></strong>
                </div>
              )}
              {handleSetPhoneTextPreview(textPreview, notSupported) && (
                <div className='inner'>
                  <p
                    style={{ whiteSpace: textHtml ? 'normal' : 'pre-line' }}
                    dangerouslySetInnerHTML={{ __html: handleSetPhoneTextPreview(textPreview, notSupported) }}
                  ></p>
                  {textPreview.footer &&(
                    <span className='footer_whatsapp_whatsapp'>{typeof textPreview.footer === 'object' ? textPreview.footer.content : textPreview.footer}</span>
                  )}
                </div>
              )}
              {values.buttons && values.buttons.length ? (
                <div className='inner-buttons'>
                  {values.buttons.map((it, index) => (
                    <div className='buttons_whatsapp_whatsapp' key={`button-${index}`}>
                      <a href={it.url ? it.url : it.content} target='blank'>
                        {it.textBtn || it.label}
                      </a>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          )}
          <div className={`${currentChannel}`}>
            {values.btnQuickReply && values.btnQuickReply.length ? (
              <div className='inner-buttons'>
                {values.btnQuickReply.map((it, index) => (
                  <div className='buttons_whatsapp_whatsapp' key={`button-${index}`}>
                    <div>
                      <span>{it}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </>
      )
    } else if (currentChannel === 'chatmail') {
      return (
        <div className={`${currentChannel}`}>
          {files && !files.withVirus && files.mimeType === 'image' && (
            <figure key={files.urlLong} className='chatmail-image'>
              <img src={files.urlLong} alt={currentChannel} />
            </figure>
          )}
          {handleSetPhoneTextPreview(textPreview, notSupported) && (
            <div className='chatmail-content'>
              <p
                style={{ whiteSpace: textHtml ? 'normal' : 'pre-line' }}
                dangerouslySetInnerHTML={{ __html: handleSetPhoneTextPreview(textPreview, notSupported) }}
              ></p>
            </div>
          )}
          {values.buttons && values.buttons.length > 0 && (
            <div className='chatmail-buttons-container'>
              {values.buttons.map((it, index) => (
                <button key={index} className='chatmail-button text-ellipsis'>
                  {it.label}
                </button>
              ))}
            </div>
          )}
          {values.signature && (
            <div className='chatmail-signature'>
              <div
                dangerouslySetInnerHTML={{
                  __html: values.signature
                }}
              ></div>
            </div>
          )}
          {handleSetPhoneTextPreview(textPreview, notSupported) && (
            <div className='chatmail-sender'>
              <p>{lang('chatmail_sender_default')}</p>
            </div>
          )}
        </div>
      )
    } else if (currentChannel === 'chatweb' || currentChannel === 'android' || currentChannel === 'ios') {
      return (
        <div className={`rectangle ${currentChannel} ${currentChannel !== 'chatweb' ? 'chatweb' : ''}`}>
          {files && !files.withVirus && files.mimeType === 'image' && (
            <div key={files.urlLong} className='image'>
              <img src={files.urlLong} alt={currentChannel} />
            </div>
          )}
          {files && !files.withVirus && files.mimeType === 'video' && (
            <div key={files.urlLong} className='image'>
              <video key={files.urlLong} src={files.urlLong} controls />
            </div>
          )}
          {files &&  !files.withVirus && files.mimeType !== 'video' && files.mimeType !== 'image' && (
            <div key={files.urlLong} className='file'>
              <img src={'./img/pdf-default.png'} alt={currentChannel} />
            </div>
          )}
          {handleSetPhoneTextPreview(textPreview, notSupported) && (
            <div className='inner'>
              <p
                style={{ whiteSpace: textHtml ? 'normal' : 'pre-line' }}
                dangerouslySetInnerHTML={{ __html: handleSetPhoneTextPreview(textPreview, notSupported) }}
              ></p>
            </div>
          )}
          {values.buttons && values.buttons.length ? (
            <div className='inner-buttons'>
              {values.buttons.map((it, index) => (
                <div className='buttons' key={`button-${index}`} style={primaryColor && currentChannel !== 'chatweb' ? { backgroundColor: primaryColor } : null}>
                  <span>{it.label}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      )
    } else if (currentChannel === 'instagram') {
      return (
        <>
          <div className={`rectangle ${currentChannel}`}>
            {files && !files.withVirus && files.mimeType === 'image' && values.buttons && values.buttons.length ? (
              <div key={files.urlLong} className='image'>
                <img src={files.urlLong} alt={currentChannel} />
              </div>
            ) : null}
            {files && !files.withVirus && files.mimeType === 'video' && values.buttons && values.buttons.length ? (
              <div key={files.urlLong} className='image'>
                <video key={files.urlLong} src={files.urlLong} controls />
              </div>
            ) : null}
            {files && !files.withVirus && files.mimeType !== 'video' && files.mimeType !== 'image' && (
              <div key={files.urlLong} className='file'>
                <img src={'./img/pdf-default.png'} alt={currentChannel} />
              </div>
            )}
            {handleSetPhoneTextPreview(textPreview, notSupported) && (
              <div className='inner'>
                <p
                  style={{ whiteSpace: textHtml ? 'normal' : 'pre-line' }}
                  dangerouslySetInnerHTML={{ __html: handleSetPhoneTextPreview(textPreview, notSupported) }}
                ></p>
              </div>
            )}
            {values.buttons && values.buttons.length ? (
              <div className='inner-buttons'>
                {values.buttons.map((it, index) => (
                  <div className='buttons' key={`button-${index}`} style={primaryColor && currentChannel !== 'chatweb' ? { backgroundColor: primaryColor } : null}>
                    <span>{it.label}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
          <div className={`rectangle ${currentChannel}`}>
            {files && !files.withVirus && files.mimeType === 'image' && values.buttons && !values.buttons.length ? (
              <div key={files.urlLong} className='image'>
                <img src={files.urlLong} alt={currentChannel} />
              </div>
            ) : null}
            {files && !files.withVirus && files.mimeType === 'video' && values.buttons && !values.buttons.length ? (
              <div key={files.urlLong} className='image'>
                <video key={files.urlLong} src={files.urlLong} controls />
              </div>
            ) : null}
          </div>
        </>
      )
    } else {
      return null
    }
  }

  const channels = () => {
    const channels = Object.keys(MARKETING.channels).filter(it => it !== 'wall')  // ['facebook', 'whatsapp', 'sms', 'telegram']
    const channelsToShow = []
    channels.forEach((_channel) => {
      channelsToShow.push(
        <img
          key={_channel}
          src={`${getStaticImage(`/channels/orange-brand/${_channel}-icon.svg`)}`}
          alt={_channel}
          className={`${currentChannel !== _channel && 'not-active'}`}
          onClick={() => { setCurrentChannel(_channel); setCurrentChannelPreview(_channel) }}
        />
      )
    })
    return <div className='channel-selector'>{channelsToShow}</div>
  }

  return (
    <div className={`phone-background align-items-end pb-0 mb-0 ${channelSelector && 'with-channels'}`}>
      <legend className='disclaimer'>{lang('phoneviewer_disclaimer')}</legend>
      {!previewMsg && channelSelector && channels()}
      <div className='phone-container'>
        <div style={{ height: '100%' }}>
          {currentChannel === 'chatweb'
            ? <div className='chatweb-wrapper'></div>
            : currentChannel === 'ios'
              ? <img src={`${getStaticImage(`/iphoneX-portrait-overlay.png`)}`} alt='iPhone X portrait' width='270px' />
              : currentChannel === 'android'
                ? <img src={`${getStaticImage(`/android-portrait-overlay.png`)}`} alt='Android portrait' width='270px' />
                : currentChannel !== 'chatmail'
                  ? <img src={`./img/iphonex-full-${currentChannel ? currentChannel.toLowerCase() : 'facebook'}.png`} alt='' width='270px' />
                  : null
          }

          {variables.length > 0 && currentChannel && (currentChannel.toLowerCase() === 'facebook' || currentChannel.toLowerCase() === 'chatweb1') && (
            <div className='company'>
              <img className={`company ${currentChannel}`} src={company.icon} alt={company.text} />
            </div>
          )}

          {currentChannel === 'chatweb' ? (
            <div className={`header-chatweb ${currentChannel}`}>
              {company
                ? <img className={`company ${currentChannel}`} src={company.icon} alt={company.text} />
                : <div className={`company ${currentChannel}`} />
              }
              <div className='info'>
                <span className='name'>Bottie</span>
                <span className='company-name'>{company ? company.name : companies.name}</span>
              </div>
            </div>
          ) : null}

          {currentChannel === 'chatmail' && (
            <div className='chatmail-header'>
              {textPreview.subject && <p className='chatmail-header__subject'>{textPreview.subject}</p>}
              <div className='chatmail-header__content'>
                <figure className='chatmail-header__avatar'>
                  <img src={getStaticImage('/icons/orange-brand/icon-chatmail.svg')} alt='ChatMail avatar' />
                </figure>
                <div className='chatmail-header__info'>
                  <p><strong>{lang('dashboard_support')}</strong><span>{`<${lang('dashboard_support')}@nixi1.com>`}</span></p>
                  <div className='chatmail-header__addressee'>
                    <span>{lang('dashboard_toMe')}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {currentChannel === 'android' || currentChannel === 'ios' ? (
            <div className={`header-app ${currentChannel}`}>
              <figure className='header-app__icon'>
                <img src={`${getStaticImage(`/apps/chats.svg`)}`} alt='Messages' />
              </figure>
              <figure className='header-app__icon'>
                <img src={`${getStaticImage(`/apps/favorites.svg`)}`} alt='Favorites' />
              </figure>
              <figure className='header-app__icon'>
                <img src={`${getStaticImage(`/apps/profile.svg`)}`} alt='Profile' />
              </figure>
            </div>
          ) : null}

          {currentChannel === 'android' || currentChannel === 'ios' ? (
            <div className={`body-app ${currentChannel}`}>
              <div className={`phone-content scrollbar ${currentChannel}`}>
                {textPreview && textPreview.channelAppPreview
                  ? <ChannelAppPreview channel={currentChannel} primaryColor={primaryColor} />
                  : preview(textPreview)
                }
              </div>
            </div>
          ) : (
            <div className={`phone-content scrollbar ${currentChannel}`}>
              {textPreview && currentChannel !== 'chatweb' && currentChannel !== 'chatmail' && origin !== 'marketing'
                ? previewFragment(textPreview)
                : preview(textPreview)
              }
            </div>
          )}

          {currentChannel === 'chatweb' ? (
            <div className={`textbox-chatweb ${currentChannel}`}>
              <span>{lang('dashboard_write-here')}...</span>
              <div className='textbox-chatweb__controls'>
                <figure className='textbox-chatweb__icon'>
                  <img src={`${getStaticImage(`/apps/camera.svg`)}`} alt='Camera' />
                </figure>
                <figure className='textbox-chatweb__icon'>
                  <img src={`${getStaticImage(`/apps/mic.svg`)}`} alt='Microphone' />
                </figure>
                <figure className='textbox-chatweb__icon'>
                  <img src={`${getStaticImage(`/apps/emoji.svg`)}`} alt='Emojis' />
                </figure>
                <figure className='textbox-chatweb__icon'>
                  <img src={`${getStaticImage(`/apps/file.svg`)}`} alt='Files' />
                </figure>
              </div>
            </div>
          ) : null}
          {currentChannel === 'android' || currentChannel === 'ios' ? (
            <div className={`footer-app ${currentChannel}`}>
              <span>{lang('dashboard_write-here')}...</span>
              <div className='footer-app__controls'>
                <figure className='footer-app__icon'>
                  <svg width="20px" height="19px" viewBox="0 0 20 19">
                    <g id="UX" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                      <g id="05-chat" transform="translate(-278.000000, -824.000000)" fill={primaryColor ? primaryColor : '#E87721'} fillRule="nonzero">
                        <g id="chat" transform="translate(0.000000, 804.000000)">
                          <g id="Group-2" transform="translate(278.000000, 19.000000)">
                            <g id="camera" transform="translate(0.000000, 1.000000)">
                              <path d="M3,2.5 L5,2.5 L5,2.5 C5.55555556,0.833333333 6.38888889,0 7.5,0 C9.16666667,0 10.8333333,0 12.5,0 C13.6111111,0 14.4444444,0.833333333 15,2.5 L17,2.5 C18.6568542,2.5 20,3.84314575 20,5.5 L20,15.3333333 C20,16.9901876 18.6568542,18.3333333 17,18.3333333 L3,18.3333333 C1.34314575,18.3333333 -2.41183085e-16,16.9901876 0,15.3333333 L0,5.5 C-6.46995335e-16,3.84314575 1.34314575,2.5 3,2.5 Z M10,15 C12.7614237,15 15,12.7614237 15,10 C15,7.23857625 12.7614237,5 10,5 C7.23857625,5 5,7.23857625 5,10 C5,12.7614237 7.23857625,15 10,15 Z M10,13.3333333 C11.8409492,13.3333333 13.3333333,11.8409492 13.3333333,10 C13.3333333,8.15905083 11.8409492,6.66666667 10,6.66666667 C8.15905083,6.66666667 6.66666667,8.15905083 6.66666667,10 C6.66666667,11.8409492 8.15905083,13.3333333 10,13.3333333 Z" id="Shape"></path>
                            </g>
                          </g>
                        </g>
                      </g>
                    </g>
                  </svg>
                </figure>
                <figure className='footer-app__icon'>
                  <svg width="15px" height="21px" viewBox="0 0 15 21">
                    <g id="UX" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                      <g id="05-chat" transform="translate(-315.000000, -824.000000)" fill={primaryColor ? primaryColor : '#E87721'} fillRule="nonzero">
                        <g id="chat" transform="translate(0.000000, 804.000000)">
                          <g id="Group-2" transform="translate(278.000000, 19.000000)">
                            <g id="mic" transform="translate(37.000000, 1.000000)">
                              <path d="M7.14463743,14.2812419 C8.235736,14.2812419 9.1694746,13.892792 9.94633087,13.1162397 C10.7230569,12.3396874 11.1114633,11.4054711 11.1114633,10.3144594 L11.1114633,3.96721667 C11.1114633,2.87620495 10.7233608,1.9423795 9.94633087,1.16543639 C9.1694746,0.388666956 8.235736,0 7.14463743,0 C6.05366913,0 5.11980026,0.388666956 4.34290056,1.16543639 C3.56595745,1.94224924 3.17755102,2.87620495 3.17755102,3.96721667 L3.17755102,10.3144594 C3.17755102,11.4054277 3.56613113,12.3396874 4.34290056,13.1162397 C5.11967,13.892792 6.05366913,14.2812419 7.14463743,14.2812419 Z" id="Path"></path>
                              <path d="M14.0496309,8.16977855 C13.8930525,8.01272254 13.7066869,7.93417282 13.4917933,7.93417282 C13.2771168,7.93417282 13.0911854,8.01272254 12.9339557,8.16977855 C12.7770734,8.32679114 12.6985671,8.51272254 12.6985671,8.72752931 L12.6985671,10.3144594 C12.6985671,11.8435085 12.1550152,13.1513244 11.0681719,14.2381242 C9.98167607,15.3249674 8.6736865,15.8683891 7.14459401,15.8683891 C5.61554494,15.8683891 4.30768563,15.3249674 3.22079896,14.2381242 C2.13404255,13.1516283 1.59070777,11.8435519 1.59070777,10.3144594 L1.59070777,8.72752931 C1.59070777,8.51272254 1.51215805,8.32679114 1.35518888,8.16977855 C1.19813287,8.01272254 1.01237516,7.93417282 0.797351281,7.93417282 C0.582327399,7.93417282 0.396352584,8.01272254 0.239383413,8.16977855 C0.0822839774,8.32679114 0.00377768129,8.51272254 0.00377768129,8.72752931 L0.00377768129,10.3144594 C0.00377768129,12.14099 0.613417282,13.7299609 1.83234911,15.0811116 C3.05132436,16.4323057 4.55757707,17.2070777 6.35115067,17.4052974 L6.35115067,19.0419019 L3.1775076,19.0419019 C2.96261398,19.0419019 2.77668259,19.1205384 2.61967,19.2775944 C2.46261398,19.4345202 2.38406426,19.6205384 2.38406426,19.835432 C2.38406426,20.0499783 2.46261398,20.2363439 2.61967,20.3932696 C2.77668259,20.5501954 2.96261398,20.6289622 3.1775076,20.6289622 L11.1114633,20.6289622 C11.3263135,20.6289622 11.5125054,20.5502388 11.6693009,20.3932696 C11.8265306,20.2363873 11.9051238,20.0500217 11.9051238,19.835432 C11.9051238,19.6205818 11.8265306,19.4345636 11.6693009,19.2775944 C11.5125488,19.1205384 11.3263569,19.0419019 11.1114633,19.0419019 L7.93821103,19.0419019 L7.93821103,17.4052974 C9.73148068,17.2070777 11.2376031,16.4323057 12.4567086,15.0811116 C13.6758142,13.7299609 14.2856274,12.14099 14.2856274,10.3144594 L14.2856274,8.72752931 C14.2856274,8.51276596 14.206904,8.32696483 14.0496309,8.16977855 Z" id="Path"></path>
                            </g>
                          </g>
                        </g>
                      </g>
                    </g>
                  </svg>
                </figure>
                <figure className='footer-app__icon'>
                  <svg width="20px" height="20px" viewBox="0 0 20 20">
                    <g id="UX" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                      <g id="05-chat" transform="translate(-345.000000, -824.000000)">
                        <g id="chat" transform="translate(0.000000, 804.000000)">
                          <g id="Group-2" transform="translate(278.000000, 19.000000)">
                            <g id="emoji" transform="translate(67.000000, 1.000000)">
                              <circle id="Oval" fill={primaryColor ? primaryColor : '#E87721'} cx="10" cy="10" r="10"></circle>
                              <circle id="Oval" fill="#FFFFFF" cx="6.5" cy="7.5" r="1.5"></circle>
                              <circle id="Oval-Copy" fill="#FFFFFF" cx="13.5" cy="7.5" r="1.5"></circle>
                              <path d="M6.30506801,12.5348739 C6.90700816,13.982289 8.33465451,15 10,15 C11.656443,15 13.0777214,13.9931407 13.6852079,12.5580495" id="Path" stroke="#FFFFFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                            </g>
                          </g>
                        </g>
                      </g>
                    </g>
                  </svg>
                </figure>
                <figure className='footer-app__icon'>
                  <svg width="14px" height="22px" viewBox="0 0 14 22">
                    <g id="UX" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                      <g id="05-chat" transform="translate(-380.000000, -823.000000)" fill={primaryColor ? primaryColor : '#E87721'} fillRule="nonzero">
                        <g id="chat" transform="translate(0.000000, 804.000000)">
                          <g id="Group-2" transform="translate(278.000000, 19.000000)">
                            <g id="file" transform="translate(102.000000, 0.000000)">
                              <path d="M6.875,22 C3.0836792,22 0,18.9163208 0,15.125 L0,5.5 C0,4.99310303 0.410720836,4.58338929 0.916610707,4.58338929 C1.42266846,4.58338929 1.83338929,4.99310303 1.83338929,5.5 L1.83338929,15.125 C1.83338929,17.9043732 4.0947876,20.1666107 6.875,20.1666107 C9.6552124,20.1666107 11.9166107,17.9043732 11.9166107,15.125 L11.9166107,5.04161071 C11.9166107,3.2725067 10.4774933,1.83338929 8.70838929,1.83338929 C6.93911745,1.83338929 5.5,3.2725067 5.5,5.04161071 L5.5,14.2083893 C5.5,14.9663849 6.11683656,15.5833893 6.875,15.5833893 C7.63316344,15.5833893 8.25,14.9663849 8.25,14.2083893 L8.25,5.5 C8.25,4.99310303 8.66072084,4.58338929 9.16661071,4.58338929 C9.67266846,4.58338929 10.0833893,4.99310303 10.0833893,5.5 L10.0833893,14.2083893 C10.0833893,15.9774933 8.644104,17.4166107 6.875,17.4166107 C5.105896,17.4166107 3.66661071,15.9774933 3.66661071,14.2083893 L3.66661071,5.04161071 C3.66661071,2.26240541 5.92800905,0 8.70838929,0 C11.4886017,0 13.75,2.26240541 13.75,5.04161071 L13.75,15.125 C13.75,18.9163208 10.6663208,22 6.875,22 Z" id="Path"></path>
                            </g>
                          </g>
                        </g>
                      </g>
                    </g>
                  </svg>
                </figure>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
